import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'js/design_system/Button';
import { Icon } from 'js/design_system/Icon';
import styled from 'astroturf/react';
import { getDefaultSortSettings } from '../utils/sortDefaults';

const Container = styled.div`
  position: relative;
  display: inline-block;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 200px;
  background-color: white;
  border: 1px solid #e1e4e8;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(149, 157, 165, 0.2);
  z-index: 1000;
  padding: 4px 0;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 8px 16px;
  border: none;
  background: none;
  text-align: left;
  font-size: 14px;
  color: #24292e;
  cursor: pointer;

  &:hover {
    background-color: #f6f8fa;
  }

  &.active {
    background-color: #0366d6;
    color: white;
  }
`;

export interface SortSettings {
  field:
    | 'citationCount'
    | 'impactFactor'
    | 'publicationYear'
    | 'relevance'
    | 'work_index'
    | 'relevance_score';
  direction: 'asc' | 'desc';
}

interface SortPanelProps {
  onSortChange?: (settings: SortSettings) => void;
  currentSort?: SortSettings | null;
  hasRerankData?: boolean;
}

const SortPanel: React.FC<SortPanelProps> = ({
  onSortChange,
  currentSort,
  hasRerankData = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Sort options with simplified labels
  const sortOptions = [
    ...(hasRerankData
      ? [
          {
            field: 'work_index' as const,
            direction: 'asc' as const,
            label: 'Default relevance',
          },
          {
            field: 'relevance_score' as const,
            direction: 'desc' as const,
            label: 'Academia relevance',
          },
        ]
      : [
          {
            field: 'relevance' as const,
            direction: 'desc' as const,
            label: 'Most relevant',
          },
        ]),
    {
      field: 'citationCount' as const,
      direction: 'desc' as const,
      label: 'Most cited',
    },
    {
      field: 'publicationYear' as const,
      direction: 'desc' as const,
      label: 'Newest first',
    },
    {
      field: 'publicationYear' as const,
      direction: 'asc' as const,
      label: 'Oldest first',
    },
    {
      field: 'impactFactor' as const,
      direction: 'desc' as const,
      label: 'Highest journal impact',
    },
  ];

  // Get current active settings (either user-defined or default)
  const activeSettings: SortSettings = currentSort || getDefaultSortSettings();

  // Get current sort display text
  const getCurrentSortText = () => {
    const option = sortOptions.find(
      (opt) =>
        opt.field === activeSettings.field &&
        opt.direction === activeSettings.direction,
    );
    return option?.label || 'Sort';
  };

  // Check if option is currently active
  const isOptionActive = (field: string, direction: string) => {
    return (
      activeSettings.field === field && activeSettings.direction === direction
    );
  };

  // Handle sort selection
  const handleSortSelect = (
    field: SortSettings['field'],
    direction: SortSettings['direction'],
  ) => {
    if (onSortChange) {
      onSortChange({ field, direction });
    }
    setIsOpen(false);
  };

  return (
    <Container ref={containerRef}>
      {/* Sort Button */}
      <Button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        size="xs"
        icon={<Icon name="swap_vert" size={16} />}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`Sort results: ${getCurrentSortText()}`}
      >
        <span>Sort: {getCurrentSortText()}</span>
        <Icon name="expand_more" size={16} style={{ marginLeft: '4px' }} />
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <Dropdown role="listbox">
          {sortOptions.map((option) => (
            <DropdownItem
              key={`${option.field}-${option.direction}`}
              onClick={() => handleSortSelect(option.field, option.direction)}
              className={
                isOptionActive(option.field, option.direction) ? 'active' : ''
              }
              role="option"
              aria-selected={isOptionActive(option.field, option.direction)}
            >
              {option.label}
            </DropdownItem>
          ))}
        </Dropdown>
      )}
    </Container>
  );
};

export default SortPanel;
